import React from 'react';
import PropTypes from 'prop-types';
import styled, { keyframes } from 'styled-components';
import { useTranslation } from 'react-i18next';
import { mediaBelow, pxToRem } from '../../assets/styles/helper';
import { variables } from '../../assets/styles/variables';
import themeColors from '../../assets/styles/themeColors';
import Button from '../Button/Button';
import IconButton from '../IconButton/IconButton';
import { ReactComponent as Close } from '../../assets/images/priceAlerts/alert-close-icon.svg';

const slideUp = keyframes`
  from { transform: translateY(100%); }
  to { transform: translateY(0); }
`;

const Overlay = styled.div`
  position: absolute;
  inset: 0;
  background: ${themeColors.colorModalBackground};
  display: flex;
  align-items: flex-end;
  justify-content: center;
  z-index: ${variables.zIndex.indexXxxl};
`;

const Card = styled.div`
  background: ${themeColors.colorWhite};
  border-radius: ${variables.borderRadius.borderRadiusLg}
    ${variables.borderRadius.borderRadiusLg} 0 0;
  box-shadow: 0 -12px 24px rgba(0, 0, 0, 0.12);
  padding: ${pxToRem(24)};
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: ${pxToRem(20)};
  animation: ${slideUp} 180ms ease-out;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const Title = styled.h3`
  margin: 0;
  font-family: ${variables.family.goldSilver};
  font-size: 14px;
  font-weight: 600;
  line-height: 1.3;
  color: ${themeColors.colorTextPrimary};

  ${mediaBelow(variables.breakpoints.bpMd)} {
    font-size: 14px;
    line-height: 100%;
  }
`;

const CloseButton = styled(IconButton)`
  &,
  svg {
    width: ${pxToRem(16)};
    height: ${pxToRem(16)};
  }
`;

const Message = styled.p`
  margin: 0;
  font-size: 14px;
  line-height: 1.4;
  color: ${themeColors.colorTextSecondary};

  ${mediaBelow(variables.breakpoints.bpMd)} {
    font-weight: 500;
  }
`;

const Actions = styled.div`
  display: flex;
  gap: ${pxToRem(12)};

  button {
    flex: 1;
    font-size: 16px;
  }

  ${mediaBelow(variables.breakpoints.bpMd)} {
    button {
      height: 44px;
      font-size: 16px;
      font-weight: 600;
      line-height: 100%;
    }
  }
`;

const DeleteAlertModal = ({ open, targetPrice, onCancel, onConfirm }) => {
  const { t } = useTranslation();

  if (!open) return null;

  return (
    <Overlay onClick={onCancel} data-cy="delete-alert-modal">
      <Card onClick={(e) => e.stopPropagation()}>
        <Header>
          <Title>{t('gtt.title.deleteAlert')}</Title>
          <CloseButton onClick={onCancel} data-cy="button-delete-alert-close">
            <Close />
          </CloseButton>
        </Header>
        <Message>{t('gtt.deleteAlert.body', { targetPrice })}</Message>
        <Actions>
          <Button
            variant="primaryOutlined"
            textTransform="none"
            label={t('common.cancel')}
            onClick={onCancel}
            data-cy="button-delete-alert-cancel"
          />
          <Button
            variant="primary"
            textTransform="none"
            label={t('gtt.common.deleteAlert')}
            onClick={onConfirm}
            data-cy="button-delete-alert-confirm"
          />
        </Actions>
      </Card>
    </Overlay>
  );
};

DeleteAlertModal.propTypes = {
  open: PropTypes.bool,
  targetPrice: PropTypes.string,
  onCancel: PropTypes.func,
  onConfirm: PropTypes.func,
};

export default DeleteAlertModal;
